/**
 * Session helpers: the non-secret profile kept for the UI.
 *
 * The credential itself now lives in an HttpOnly cookie set by the backend, so
 * nothing here ever sees a JWT. What the till still needs is the operator's name
 * (cart keys, the signed-in label) and whether they may open the user console,
 * plus when the session ends, so an offline till can answer "is my session still
 * good?" with no server reachable.
 */

export type SessionProfile = {
  username: string;
  /** UI hint only; the server re-checks the account on every admin call. */
  is_admin: boolean;
};

/** The non-secret session profile. Never the credential. */
export const SESSION_PROFILE_STORAGE_KEY = 'cashier.sessionProfile';

/**
 * When the stored session ends, as epoch milliseconds.
 *
 * Kept on its own on purpose. The offline till has to answer "is this session
 * still good?" with no server reachable, and the JWT is no longer readable from
 * script, so this timestamp is the part of that answer that is not a secret.
 */
export const SESSION_EXPIRY_STORAGE_KEY = 'cashier.sessionExpiresAt';

function isSessionProfile(value: unknown): value is SessionProfile {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<SessionProfile>;
  return typeof candidate.username === 'string' && typeof candidate.is_admin === 'boolean';
}

/** The stored profile, or null when this device remembers no session. */
export function readStoredProfile(): SessionProfile | null {
  const raw = localStorage.getItem(SESSION_PROFILE_STORAGE_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isSessionProfile(parsed)) {
      // Corruption, not a stale session: drop it so a broken entry cannot wedge
      // the sign-in prompt forever.
      localStorage.removeItem(SESSION_PROFILE_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(SESSION_PROFILE_STORAGE_KEY);
    return null;
  }
}

export function storeSessionProfile(profile: SessionProfile): void {
  localStorage.setItem(SESSION_PROFILE_STORAGE_KEY, JSON.stringify(profile));
}

export function clearSessionProfile(): void {
  localStorage.removeItem(SESSION_PROFILE_STORAGE_KEY);
}

/** Record when the session ends, so the check never needs the credential. */
export function storeSessionExpiry(expiresAtMs: number): void {
  localStorage.setItem(SESSION_EXPIRY_STORAGE_KEY, String(expiresAtMs));
}

export function clearSessionExpiry(): void {
  localStorage.removeItem(SESSION_EXPIRY_STORAGE_KEY);
}

function readStoredExpiry(): number | null {
  const raw = localStorage.getItem(SESSION_EXPIRY_STORAGE_KEY);
  if (raw === null) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/**
 * Is the stored session expired? Drives the "sign in to sync" prompt.
 *
 * Uses only the recorded expiry timestamp: the JWT is in an HttpOnly cookie now,
 * so decoding one is not an option, and a session with no recorded timestamp is
 * treated as stale rather than trusted. An expired session is deliberately not
 * dropped: an offline till keeps ringing sales (they are queued on the device
 * regardless), and the server is what enforces a valid session when syncing.
 */
export function storedTokenExpired(): boolean {
  const expiry = readStoredExpiry();
  if (expiry === null) return true;
  return expiry <= Date.now();
}
